import { useState } from 'react';
import useApi from '../../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../../utils/UserContext';
import { ClipLoader } from 'react-spinners';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { MagnifyingGlass, CheckCircle, XCircle } from '@phosphor-icons/react';
import { Select } from 'antd';

interface CarrierLookupListProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

function CarrierLookupList({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: CarrierLookupListProps) {
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const { userId } = useUser();
  const [identifier, setIdentifier] = useState('');
  const [identifierType, setIdentifierType] = useState<'mc' | 'dot'>('mc');
  const [isLoading, setIsLoading] = useState(false);
  const [carrierExists, setCarrierExists] = useState<boolean | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    if (!identifier.trim()) {
      return;
    }

    setIsLoading(true);
    setHasSearched(false);

    try {
      const response = await callProtectedApi(
        `/customers/carrier-exists/${userId ?? ''}/${userType}`,
        'post',
        {
          identifier: identifier.trim(),
          identifierType,
        }
      );

      setCarrierExists(response?.data?.data?.exists || false);
      setHasSearched(true);
    } catch (error) {
      console.error('Error checking carrier:', error);
      setCarrierExists(false);
      setHasSearched(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full flex-col items-center justify-center px-4 sm:w-[95%] sm:py-4 md:w-full lg:w-[1148px] lg:max-w-[1148px] lg:px-0">
            <div className="w-full max-w-[600px] rounded-lg border-[1px] border-bordergray bg-white p-8">
              <h2 className="mb-6 text-center text-2xl font-semibold text-primary">
                Carrier Lookup
              </h2>

              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Identifier Type
                </label>
                <Select
                  value={identifierType}
                  onChange={(value) => {
                    setIdentifierType(value);
                    setCarrierExists(null);
                    setHasSearched(false);
                  }}
                  className="w-full"
                  size="large"
                >
                  <Select.Option value="mc">MC Number</Select.Option>
                  <Select.Option value="dot">DOT Number</Select.Option>
                </Select>
              </div>

              <div className="mb-6">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  {identifierType === 'mc' ? 'MC Number' : 'DOT Number'}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder={`Enter ${identifierType === 'mc' ? 'MC' : 'DOT'} Number`}
                    className="flex-1 rounded-lg border border-gray-300 px-4 py-3 text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    value={identifier}
                    onChange={(e) => {
                      setIdentifier(e.target.value);
                      setCarrierExists(null);
                      setHasSearched(false);
                    }}
                    onKeyPress={handleKeyPress}
                  />
                  <button
                    onClick={handleSearch}
                    disabled={!identifier.trim() || isLoading}
                    className="flex items-center gap-2 rounded-lg bg-accent px-6 py-3 text-primary transition-colors hover:opacity-80 disabled:bg-gray-400"
                  >
                    {isLoading ? (
                      <ClipLoader color="#ffffff" size={20} />
                    ) : (
                      <>
                        <MagnifyingGlass size={20} />
                        Search
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Results Section */}
              {hasSearched && !isLoading && (
                <div className="mt-8 rounded-lg border p-6">
                  {carrierExists ? (
                    <div className="flex items-center gap-3">
                      <CheckCircle
                        size={32}
                        className="text-green-600"
                        weight="fill"
                      />
                      <div>
                        <p className="text-lg font-semibold text-green-700">
                          Yes, this carrier is an active TACH member
                        </p>
                        <p className="text-sm text-gray-600">
                          {identifierType.toUpperCase()} #{identifier}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <XCircle
                        size={32}
                        className="text-red-600"
                        weight="fill"
                      />
                      <div>
                        <p className="text-lg font-semibold text-red-700">
                          No, this carrier is not an active TACH member
                        </p>
                        <p className="text-sm text-gray-600">
                          {identifierType.toUpperCase()} #{identifier}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Information Section */}
              <div className="mt-8 rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-600">
                  <strong>Note:</strong> This tool checks if a carrier is
                  currently registered as an active TACH member using their MC
                  or DOT number.
                </p>
              </div>
            </div>
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default CarrierLookupList;
